import { css, cx } from "@emotion/css";
import { PopoverBasicHelp } from "@octopusdeploy/design-system-components";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActivityStatus, KubernetesResourceManifestDiffResource, KubernetesResourceManifestSummaryResource, KubernetesResourceStatus } from "@octopusdeploy/octopus-server-client";
import React, { useCallback, useEffect, useState } from "react";
import { ManifestDiffResultIcon } from "~/areas/tasks/components/Task/Kubernetes/Manifests/ManifestDiffResultIcon";
import { ObjectStatusIcon } from "~/areas/tasks/components/Task/Kubernetes/ObjectStatusIcon";
import { TaskStatusIcon } from "../TaskStatusIcon";
import { ManifestNavigationDetails } from "./ManifestNavigationDetails";
type NavigationTree<TManifestItem extends ManifestTreeNavItem | ManifestDiffTreeNavItem> = {
    stepItems: StepTreeNavItem<TManifestItem>[];
};
export type ManifestNavigationTree = NavigationTree<ManifestTreeNavItem>;
export type ManifestDiffNavigationTree = {
    deploymentTrees: (NavigationTree<ManifestDiffTreeNavItem> & {
        executionId: string;
        name: string;
        isCurrentExecution: boolean;
    })[];
};
type StepTreeNavItem<TManifestItem extends ManifestTreeNavItem | ManifestDiffTreeNavItem> = {
    stepName: string;
    stepStatus: ActivityStatus | undefined;
    actionId: string;
    targetItems: TargetTreeNavItem<TManifestItem>[];
};
type TargetTreeNavItem<TManifestItem extends ManifestTreeNavItem | ManifestDiffTreeNavItem> = {
    machineId: string;
    targetName: string;
    namespaceItems: NamespaceTreeNavItem<TManifestItem>[];
};
type NamespaceTreeNavItem<TManifestItem extends ManifestTreeNavItem | ManifestDiffTreeNavItem> = {
    namespace: string;
    manifestItems: TManifestItem[];
};
type ManifestTreeNavItem = {
    resource: KubernetesResourceManifestSummaryResource;
    name: string;
    kind: string;
    objectStatus: KubernetesResourceStatus | undefined;
};
type ManifestDiffTreeNavItem = ManifestTreeNavItem & {
    diffResource: KubernetesResourceManifestDiffResource;
};
function isDiffNavigationTree(item: unknown): item is ManifestDiffNavigationTree {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (item as ManifestDiffNavigationTree).deploymentTrees !== undefined;
}
function isDiffTreeNavItem(item: ManifestTreeNavItem): item is ManifestDiffTreeNavItem {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (item as ManifestDiffTreeNavItem).diffResource !== undefined;
}
const styles = {
    doNotShrink: css({
        flexShrink: 0,
    }),
    navigationDeploymentLabel: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        //the PopoverBasicHelp container div has a silly -7px bottom which stuffs the alignment
        "> div": {
            bottom: "unset",
        },
    }),
    navigationDeploymentLabelText: css({
        padding: space[8],
        font: text.body.bold.medium,
    }),
    navigationTree: css({
        paddingBottom: space[16],
    }),
    targetTreeSummary: css({
        paddingLeft: space[32],
    }),
    namespacesTreeSummary: css({
        paddingLeft: space[64],
    }),
    manifestListItemList: css({
        display: "flex",
        flexDirection: "column",
        gap: space[4],
    }),
    manifestListItem: css({
        padding: space[8],
        paddingLeft: space[80],
        paddingRight: space[12],
        cursor: "pointer",
        display: "flex",
        flexDirection: "row",
        gap: space[8],
        alignItems: "flex-start",
        "&:hover": {
            borderRadius: borderRadius.small,
            backgroundColor: themeTokens.color.background.primary.hover,
        },
        "&:focus": {
            backgroundColor: themeTokens.color.background.primary.default,
            borderRadius: borderRadius.small,
            border: `1px solid ${themeTokens.color.border.primary}`,
        },
    }),
    manifestListItemSelected: css({
        borderRadius: borderRadius.small,
        backgroundColor: themeTokens.color.background.primary.pressed,
    }),
    manifestListItemIcon: css({
        paddingTop: space[4],
        flexShrink: 0,
    }),
    manifestListItemHeader: css({
        color: themeTokens.color.text.primary,
        font: text.body.regular.medium,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    manifestListItemSubtitle: css({
        color: themeTokens.color.text.secondary,
        font: text.body.regular.xSmall,
    }),
    manifestDiffIcon: css({
        marginLeft: "auto",
    }),
};
type ManifestNavigationTreeProps = {
    navigationTree: ManifestNavigationTree | ManifestDiffNavigationTree;
    onManifestSelected: (manifestId: string | undefined) => void;
};
export const ManifestNavigationTree = (props: ManifestNavigationTreeProps) => {
    const { navigationTree, onManifestSelected } = props;
    const [selectedManifestId, setSelectedManifestId] = useState<string | undefined>();
    const selectManifest = useCallback((manifestId: string | undefined) => {
        setSelectedManifestId(manifestId);
        onManifestSelected(manifestId);
    }, [onManifestSelected]);
    useEffect(() => selectManifest(undefined), [navigationTree, selectManifest]);
    const renderStepNavigationTrees = <TManifestItem extends ManifestTreeNavItem | ManifestDiffTreeNavItem>(stepItems: StepTreeNavItem<TManifestItem>[], deploymentName?: string) => stepItems.map((stepTreeItem) => (<ManifestNavigationDetails key={stepTreeItem.actionId} summary={<>
                        {stepTreeItem.stepStatus && (<span className={styles.doNotShrink}>
                                <TaskStatusIcon status={stepTreeItem.stepStatus}/>
                            </span>)}
                        <span>{stepTreeItem.stepName}</span>
                    </>}>
                <div className={styles.manifestListItemList}>
                    {stepTreeItem.targetItems.map((targetTreeItem) => (<ManifestNavigationDetails key={targetTreeItem.machineId} summary={targetTreeItem.targetName} summaryClassName={styles.targetTreeSummary}>
                            {targetTreeItem.namespaceItems.map((namespaceTreeItem) => (<ManifestNavigationDetails key={namespaceTreeItem.namespace} summary={namespaceTreeItem.namespace} summaryClassName={styles.namespacesTreeSummary}>
                                    <div className={styles.manifestListItemList}>
                                        {namespaceTreeItem.manifestItems.map((manifestTreeItem) => (<div key={manifestTreeItem.resource.Id} className={cx(styles.manifestListItem, manifestTreeItem.resource.Id === selectedManifestId ? styles.manifestListItemSelected : null)} onClick={() => selectManifest(manifestTreeItem.resource.Id)}>
                                                <div className={styles.manifestListItemIcon}>{manifestTreeItem.objectStatus && <ObjectStatusIcon objectStatus={manifestTreeItem.objectStatus} stepStatus={stepTreeItem.stepStatus}/>}</div>
                                                <div>
                                                    <div className={styles.manifestListItemHeader}>{manifestTreeItem.name}</div>
                                                    <div className={styles.manifestListItemSubtitle}>{manifestTreeItem.kind}</div>
                                                </div>
                                                {isDiffTreeNavItem(manifestTreeItem) && (<div className={styles.manifestDiffIcon}>
                                                        <ManifestDiffResultIcon diffResult={manifestTreeItem.diffResource.DiffResult} deploymentName={deploymentName || ""}/>
                                                    </div>)}
                                            </div>))}
                                    </div>
                                </ManifestNavigationDetails>))}
                        </ManifestNavigationDetails>))}
                </div>
            </ManifestNavigationDetails>));
    return (<div className={styles.navigationTree}>
            {isDiffNavigationTree(navigationTree) ? (navigationTree.deploymentTrees.map((deploymentTree) => (<div key={deploymentTree.executionId}>
                        <div className={styles.navigationDeploymentLabel}>
                            <span className={styles.navigationDeploymentLabelText}>Deployment {deploymentTree.name}</span>
                            {!deploymentTree.isCurrentExecution && (<PopoverBasicHelp description={<>
                                            <div>The deployment process changed between the two versions you’re comparing.</div>
                                            <div>This section shows manifests for steps that no longer exist in the current deployment.</div>
                                        </>} placement={"right-start"}/>)}
                        </div>
                        {renderStepNavigationTrees(deploymentTree.stepItems, navigationTree.deploymentTrees[0].name)}
                    </div>))) : (<>{renderStepNavigationTrees(navigationTree.stepItems)}</>)}
        </div>);
};
